/**
 * Enhanced Program Types for Frontend
 * Aligned with backend enhanced types while maintaining backward compatibility
 */

import { Program, Exercise, Equipment } from './index';

// ===== ENHANCED PROGRAM STRUCTURE =====

export interface EnhancedProgram extends Omit<Program, 'weeks'> {
  // Enhanced structure support
  mesocycles?: EnhancedMesocycle[];
  isEnhanced?: boolean;
  enhancedMetadata?: EnhancedGenerationMetadata;
  
  // Backward compatibility
  weeks?: ProgramWeek[]; // Keep for legacy support
}

export interface EnhancedMesocycle {
  id: string;
  name: string;
  startWeek: number;
  endWeek: number;
  adaptationGoal: AdaptationGoal;
  phases: EnhancedPhase[];
}

export interface EnhancedPhase {
  id: string;
  name: string;
  title: string;
  instructions: string;
  startWeek: number;
  endWeek: number;
  intensityProgression: IntensityProgression;
  weeks: EnhancedWeek[];
}

export interface EnhancedWeek {
  id: string;
  weekNumber: number;
  totalSets: number;
  schedule: WeekSchedule;
}

export interface WeekSchedule {
  monday: EnhancedWorkout[];
  tuesday: EnhancedWorkout[];
  wednesday: EnhancedWorkout[];
  thursday: EnhancedWorkout[];
  friday: EnhancedWorkout[];
  saturday: EnhancedWorkout[];
  sunday: EnhancedWorkout[];
}

export interface EnhancedWorkout {
  id: string;
  name: string;
  estimatedDuration: number;
  assignedExercises: EnhancedAssignedExercise[];
}

export interface EnhancedAssignedExercise {
  id: string;
  exerciseId: string;
  exerciseName: string;
  sets: EnhancedTrainingSet[];
  primaryMuscleGroups: string[];
  equipment: Record<string, number>;
}

export interface EnhancedTrainingSet {
  id: string;
  setNumber: number;
  repRangeMin: number;
  repRangeMax: number;
  targetRPE: number;
  restTime: number;
  setType: SetType;
  targetPercentage1RM: number;
  notes?: string;
}

// ===== ENHANCED LIBRARY TYPES =====

export interface DatabaseExercise {
  id: string;
  name: string;
  type: string;
  difficulty?: number;
  rating?: number;
  danger?: number;
  symmetryMode?: string;
  repRangeMin?: number;
  repRangeMax?: number;
  recommendedSets?: number;
  recommendedRestTime?: number;
  instructions?: string[];
  techniqueChecklist?: string[];
  isActive: boolean;
  muscleActivations: MuscleActivationData[];
  equipmentRequirements: EquipmentRequirementData[];
  adaptations: AdaptationData[];
}

export interface MuscleActivationData {
  activationValue: number;
  muscleGroup: {
    id: string;
    name: string;
  };
}

export interface EquipmentRequirementData {
  quantity: number;
  isRequired: boolean;
  equipmentCategory: {
    id: string;
    name: string;
    type: string;
  };
}

export interface AdaptationData {
  effectivenessValue: number;
  adaptation: {
    id: string;
    name: string;
  };
}

export interface DatabaseMuscleGroup {
  id: string;
  name: string;
  anatomicalRegion?: string;
  split?: boolean;
  sourceIdLeft?: string;
  sourceIdRight?: string;
  isActive: boolean;
}

export interface DatabaseEquipmentCategory {
  id: string;
  name: string;
  type: string;
  description?: string;
  isActive: boolean;
}

export interface DatabaseAdaptation {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
}

// ===== ENHANCED GENERATION TYPES =====

export interface EnhancedProgramGenerationRequest {
  clientId: string;
  coachId?: string;
  name?: string;
  description?: string;
  schedule: number[];
  totalWeeklySets: number;
  muscleGroupPreferences: Record<string, number>; // Database muscle group ID -> activation
  programLength: number;
  gender: 'male' | 'female';
  fitnessGoal?: 'strength' | 'hypertrophy' | 'endurance' | 'power' | 'general';
  experienceLevel?: 'beginner' | 'intermediate' | 'advanced';
  availableEquipment?: string[]; // Database equipment category IDs
  excludedExercises?: string[]; // Database exercise IDs
  preferences?: {
    maxWorkoutDuration?: number;
    preferredRestTime?: number;
    intensityPreference?: 'low' | 'moderate' | 'high';
    volumePreference?: 'low' | 'moderate' | 'high';
  };
  periodization?: {
    mesocycleCount?: number;
    deloadFrequency?: number;
    phaseTypes?: PhaseType[];
  };
}

export interface EnhancedProgramGenerationResponse {
  success: boolean;
  data: {
    programId: string;  // Database program ID
    program: EnhancedProgram;
    metadata: EnhancedGenerationMetadata;
  };
  processingTime: number;
  workerId: string;
}

export interface EnhancedGenerationMetadata {
  userId: string;
  generationTime: number;
  databaseLibrariesUsed: boolean;
  totalExercises: number;
  totalMuscleGroups: number;
  totalEquipmentTypes: number;
  schedule: number[];
  totalWeeklySets: number;
  programLength: number;
  algorithmVersion: string;
  timestamp: number;
}

// ===== ENUMS AND TYPES =====

export type AdaptationGoal = 
  | 'foundation' 
  | 'hypertrophy' 
  | 'strength' 
  | 'power' 
  | 'endurance' 
  | 'peaking' 
  | 'deload';

export type IntensityProgression = 
  | 'linear' 
  | 'undulating' 
  | 'conjugate' 
  | 'block' 
  | 'autoregulated';

export type SetType = 
  | 'warm_up' 
  | 'working' 
  | 'drop' 
  | 'cluster' 
  | 'rest_pause' 
  | 'pyramid' 
  | 'superset';

export type PhaseType = 
  | 'adaptation' 
  | 'accumulation' 
  | 'intensification' 
  | 'realization' 
  | 'deload';

export type EquipmentCategoryType = 
  | 'FREE_WEIGHTS' 
  | 'MACHINES' 
  | 'CARDIO' 
  | 'FUNCTIONAL' 
  | 'ACCESSORIES' 
  | 'TECHNOLOGY';

// ===== API RESPONSE TYPES =====

export interface LibrarySyncResponse {
  success: boolean;
  syncedAt: Date;
  libraries: {
    exercises: number;
    equipmentCategories: number;
    muscleGroups: number;
    adaptations: number;
  };
  error?: string;
}

export interface LibraryData {
  exercises: DatabaseExercise[];
  equipmentCategories: DatabaseEquipmentCategory[];
  muscleGroups: DatabaseMuscleGroup[];
  adaptations: DatabaseAdaptation[];
}

// ===== UI STATE TYPES =====

export interface EnhancedProgramEditorState {
  currentProgram: EnhancedProgram | null;
  selectedMesocycle: string | null;
  selectedPhase: string | null;
  selectedWeek: string | null;
  selectedWorkout: string | null;
  selectedSet: string | null;
  editMode: 'overview' | 'mesocycle' | 'phase' | 'week' | 'workout' | 'set';
  isDirty: boolean;
}

export interface MuscleActivationFormData {
  [muscleGroupId: string]: {
    name: string;
    activationValue: number;
    anatomicalRegion?: string;
    isActive: boolean;
  };
}

export interface EquipmentRequirementFormData {
  [categoryId: string]: {
    name: string;
    type: EquipmentCategoryType;
    quantity: number;
    isRequired: boolean;
    isSelected: boolean;
  };
}

// ===== VALIDATION TYPES =====

export interface EnhancedValidationResult {
  isValid: boolean;
  errors: string[];
  warnings?: string[];
  data?: any;
}

export interface ProgramStructureValidation {
  hasValidMesocycles: boolean;
  hasValidPhases: boolean;
  hasValidWeeks: boolean;
  hasValidWorkouts: boolean;
  hasSufficientVolume: boolean;
  errors: string[];
  warnings: string[];
}

// ===== EDITOR COMPONENT PROPS =====

export interface MesocycleEditorProps {
  mesocycle: EnhancedMesocycle;
  programId: string;
  onUpdate: (mesocycleId: string, updates: Partial<EnhancedMesocycle>) => void;
  onDelete: (mesocycleId: string) => void;
  onPhaseAdd: (mesocycleId: string) => void;
  readOnly?: boolean;
}

export interface PhaseEditorProps {
  phase: EnhancedPhase;
  mesocycleId: string;
  programId: string;
  onUpdate: (phaseId: string, updates: Partial<EnhancedPhase>) => void;
  onDelete: (phaseId: string) => void;
  onWeekAdd: (phaseId: string) => void;
  readOnly?: boolean;
}

export interface WeekEditorProps {
  week: EnhancedWeek;
  phaseId: string;
  programId: string;
  onUpdate: (weekId: string, updates: Partial<EnhancedWeek>) => void;
  onWorkoutAdd: (weekId: string, day: keyof WeekSchedule) => void;
  onWorkoutUpdate: (weekId: string, day: keyof WeekSchedule, workoutIndex: number, updates: Partial<EnhancedWorkout>) => void;
  readOnly?: boolean;
}

export interface SetEditorProps {
  set: EnhancedTrainingSet;
  exerciseId: string;
  workoutId: string;
  onUpdate: (setId: string, updates: Partial<EnhancedTrainingSet>) => void;
  onDelete: (setId: string) => void;
  readOnly?: boolean;
}

// ===== LIBRARY COMPONENT PROPS =====

export interface MuscleActivationSelectorProps {
  muscleGroups: DatabaseMuscleGroup[];
  selectedActivations: MuscleActivationFormData;
  onActivationChange: (muscleGroupId: string, activationValue: number) => void;
  showBilateralOptions?: boolean;
  showActivationValues?: boolean;
  readOnly?: boolean;
}

export interface EquipmentRequirementSelectorProps {
  categories: DatabaseEquipmentCategory[];
  selectedRequirements: EquipmentRequirementFormData;
  onRequirementChange: (categoryId: string, quantity: number, required: boolean) => void;
  showQuantities?: boolean;
  showRequiredFlags?: boolean;
  readOnly?: boolean;
}

// ===== NAVIGATION AND UTILITY TYPES =====

export interface ProgramNavigationPath {
  programId: string;
  mesocycleId?: string;
  phaseId?: string;
  weekId?: string;
  workoutId?: string;
  setId?: string;
}

export interface ProgramBreadcrumb {
  level: 'program' | 'mesocycle' | 'phase' | 'week' | 'workout' | 'set';
  id: string;
  name: string;
  path: ProgramNavigationPath;
}

// ===== CONVERSION UTILITIES =====

export interface ProgramConverter {
  enhancedToLegacy: (enhanced: EnhancedProgram) => Program;
  legacyToEnhanced: (legacy: Program) => EnhancedProgram;
  isEnhanced: (program: Program | EnhancedProgram) => program is EnhancedProgram;
}

// ===== TYPE GUARDS =====

export const isEnhancedProgram = (program: Program | EnhancedProgram): program is EnhancedProgram => {
  return 'mesocycles' in program && Array.isArray(program.mesocycles);
};

export const isEnhancedFitEngineResponse = (obj: any): obj is EnhancedProgramGenerationResponse => {
  return obj &&
    typeof obj.success === 'boolean' &&
    obj.data &&
    obj.data.program &&
    typeof obj.processingTime === 'number' &&
    typeof obj.workerId === 'string';
};

export const isDatabaseExercise = (exercise: Exercise | DatabaseExercise): exercise is DatabaseExercise => {
  return 'muscleActivations' in exercise && 
         Array.isArray(exercise.muscleActivations) &&
         'equipmentRequirements' in exercise &&
         Array.isArray(exercise.equipmentRequirements);
};

// ===== CONSTANTS =====

export const ENHANCED_PROGRAM_CONSTANTS = {
  DEFAULT_MESOCYCLE_DURATION: 4, // weeks
  DEFAULT_PHASE_DURATION: 2, // weeks
  DEFAULT_SET_REST_TIME: 60, // seconds
  DEFAULT_TARGET_RPE: 7,
  DEFAULT_TARGET_1RM_PERCENTAGE: 75,
  MIN_ACTIVATION_VALUE: 0.1,
  MAX_ACTIVATION_VALUE: 1.0,
  DEFAULT_ACTIVATION_VALUE: 0.5,
} as const;

export const ADAPTATION_GOALS: Record<AdaptationGoal, { name: string; description: string; color: string }> = {
  foundation: { 
    name: 'Foundation', 
    description: 'Build movement patterns and base fitness', 
    color: '#8B5CF6' 
  },
  hypertrophy: { 
    name: 'Hypertrophy', 
    description: 'Muscle growth and size increase', 
    color: '#10B981' 
  },
  strength: { 
    name: 'Strength', 
    description: 'Maximum strength development', 
    color: '#F59E0B' 
  },
  power: { 
    name: 'Power', 
    description: 'Explosive power and speed', 
    color: '#EF4444' 
  },
  endurance: { 
    name: 'Endurance', 
    description: 'Muscular endurance and stamina', 
    color: '#3B82F6' 
  },
  peaking: { 
    name: 'Peaking', 
    description: 'Peak performance preparation', 
    color: '#F97316' 
  },
  deload: { 
    name: 'Deload', 
    description: 'Recovery and adaptation', 
    color: '#6B7280' 
  }
} as const;

export const INTENSITY_PROGRESSIONS: Record<IntensityProgression, { name: string; description: string }> = {
  linear: { 
    name: 'Linear', 
    description: 'Steady progressive increase in intensity' 
  },
  undulating: { 
    name: 'Undulating', 
    description: 'Varying intensity within and between weeks' 
  },
  conjugate: { 
    name: 'Conjugate', 
    description: 'Multiple training methods simultaneously' 
  },
  block: { 
    name: 'Block', 
    description: 'Focused training blocks with distinct adaptations' 
  },
  autoregulated: { 
    name: 'Autoregulated', 
    description: 'Adjusts based on daily readiness and RPE' 
  }
} as const;

export const SET_TYPES: Record<SetType, { name: string; description: string; color: string }> = {
  warm_up: { 
    name: 'Warm-up', 
    description: 'Preparation sets with lighter loads', 
    color: '#FEF3C7' 
  },
  working: { 
    name: 'Working', 
    description: 'Main training sets at target intensity', 
    color: '#D1FAE5' 
  },
  drop: { 
    name: 'Drop Set', 
    description: 'Continued set with reduced weight', 
    color: '#FECACA' 
  },
  cluster: { 
    name: 'Cluster', 
    description: 'Short rest between mini-sets', 
    color: '#DDD6FE' 
  },
  rest_pause: { 
    name: 'Rest-Pause', 
    description: 'Brief rest then continue to failure', 
    color: '#FED7AA' 
  },
  pyramid: { 
    name: 'Pyramid', 
    description: 'Progressive weight increase/decrease', 
    color: '#BFDBFE' 
  },
  superset: { 
    name: 'Superset', 
    description: 'Back-to-back exercises without rest', 
    color: '#F3E8FF' 
  }
} as const;

// ===== BACKWARD COMPATIBILITY TYPES =====

export interface ProgramWeek {
  weekNumber: number;
  workouts: Workout[];
  notes?: string;
  deloadWeek: boolean;
}

export interface Workout {
  id: string;
  name: string;
  description?: string;
  day: number;
  warmup: Exercise[];
  mainWork: Exercise[];
  cooldown: Exercise[];
  estimatedDuration: number;
  difficulty: number;
  equipment: Equipment[];
  assignedDate?: string | Date;
  completed: boolean;
  completedAt?: Date;
  rating?: number;
  notes?: string;
  actualDuration?: number;
}

// ===== FORM DATA TYPES =====

export interface EnhancedProgramGenerationFormData {
  // Client selection
  clientId: string;
  
  // Basic program info
  name: string;
  description?: string;
  programLength: number;
  startDate: string;
  endDate?: string;
  
  // Training schedule
  schedule: number[];
  totalWeeklySets: number;
  
  // Client characteristics
  gender: 'male' | 'female';
  fitnessLevel: 'beginner' | 'intermediate' | 'advanced';
  
  // Program goals
  primaryGoal: AdaptationGoal;
  secondaryGoals?: AdaptationGoal[];
  
  // Muscle targeting (enhanced)
  muscleActivations: MuscleActivationFormData;
  
  // Equipment (enhanced)
  equipmentRequirements: EquipmentRequirementFormData;
  
  // Periodization
  periodization: {
    mesocycleCount: number;
    deloadFrequency: number;
    phaseTypes: PhaseType[];
    progressionPattern: IntensityProgression;
  };
  
  // Preferences
  preferences: {
    maxWorkoutDuration: number;
    preferredRestTime: number;
    intensityPreference: 'low' | 'moderate' | 'high';
    volumePreference: 'low' | 'moderate' | 'high';
    exerciseVariety: 'minimal' | 'moderate' | 'high';
  };
  
  // Constraints
  excludedExercises: string[];
  injuryConsiderations: string[];
}

// ===== ERROR TYPES =====

export interface EnhancedGenerationError {
  code: string;
  message: string;
  severity: 'low' | 'medium' | 'high';
  context?: {
    userId?: string;
    requestId?: string;
    workerId?: string;
    stage?: 'validation' | 'generation' | 'storage' | 'conversion';
    details?: any;
  };
}

export const ENHANCED_ERROR_CODES = {
  VALIDATION_FAILED: 'ENHANCED_VALIDATION_FAILED',
  DATABASE_CONNECTION_FAILED: 'DATABASE_CONNECTION_FAILED',
  LIBRARY_LOADING_FAILED: 'LIBRARY_LOADING_FAILED',
  ID_MAPPING_FAILED: 'ID_MAPPING_FAILED',
  PROGRAM_GENERATION_FAILED: 'PROGRAM_GENERATION_FAILED',
  DTO_CONVERSION_FAILED: 'DTO_CONVERSION_FAILED',
  STORAGE_FAILED: 'STORAGE_FAILED',
  LIBRARY_SYNC_FAILED: 'LIBRARY_SYNC_FAILED',
  HIERARCHICAL_EDIT_FAILED: 'HIERARCHICAL_EDIT_FAILED',
  SET_UPDATE_FAILED: 'SET_UPDATE_FAILED',
  PROGRAM_LOADING_FAILED: 'PROGRAM_LOADING_FAILED',
  PROGRAM_CREATION_FAILED: 'PROGRAM_CREATION_FAILED',
  PROGRAM_UPDATE_FAILED: 'PROGRAM_UPDATE_FAILED',
  ANALYTICS_LOADING_FAILED: 'ANALYTICS_LOADING_FAILED',
  CONVERSION_FAILED: 'CONVERSION_FAILED',
  EXPORT_FAILED: 'EXPORT_FAILED'
} as const;


